import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface MedicalCondition {
  name: string;
  description: string;
  otherDescription: string;
  checked: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (medicalHistory: string) => void;
  initialValue?: string;
}

const MEDICAL_CONDITIONS = [
  'Diabetes',
  'High Cholesterol',
  'Cancer',
  'Arthritis',
  'High Blood Pressure',
  'Heart Disease',
  'Sleep Apnea',
  'Hypothyroidism',
  'Polycystic Ovary Syndrome',
];

const MedicalHistoryModal: React.FC<Props> = ({ isOpen, onClose, onSave, initialValue }) => {
  const [conditions, setConditions] = useState<MedicalCondition[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Parse initial value if it exists
    if (initialValue) {
      try {
        const parsed = JSON.parse(initialValue);
        setConditions(parsed);
      } catch {
        // If not JSON, initialize with empty conditions
        setConditions(MEDICAL_CONDITIONS.map(name => ({
          name,
          description: '',
          otherDescription: '',
          checked: false,
        })));
      }
    } else {
      setConditions(MEDICAL_CONDITIONS.map(name => ({
        name,
        description: '',
        otherDescription: '',
        checked: false,
      })));
    }
  }, [initialValue, isOpen]);

  const handleConditionChange = (index: number, field: keyof MedicalCondition, value: any) => {
    // Debug log to verify event firing and timing
    setConditions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value } as MedicalCondition;
      return updated;
    });
  };


  const handleSave = () => {
    onSave(JSON.stringify(conditions));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent Escape key from closing the modal
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Prevent backdrop click from closing the modal
  const handleBackdropClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={() => {}} static onKeyDown={handleKeyDown} initialFocus={panelRef}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={handleBackdropClick} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel ref={panelRef} className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Medical History
                  </Dialog.Title>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="text-gray-400 hover:text-gray-600 cursor-not-allowed opacity-50"
                    title="Use Cancel button to close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 w-8">
                          <input
                            type="checkbox"
                            checked={conditions.every(c => c.checked)}
                            onChange={(e) => {
                              setConditions(prev => prev.map(c => ({ ...c, checked: e.target.checked })));
                            }}
                            className="form-checkbox"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                          Medical History
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                          Other Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {conditions.map((condition, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <input
                              key={`${condition.name}-${condition.checked ? '1' : '0'}`}
                              type="checkbox"
                              checked={condition.checked}
                              onChange={(e) => handleConditionChange(index, 'checked', e.target.checked)}
                              className="form-checkbox"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {condition.name}
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={condition.description}
                              onChange={(e) => handleConditionChange(index, 'description', e.target.value)}
                              placeholder="Enter description"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={condition.otherDescription}
                              onChange={(e) => handleConditionChange(index, 'otherDescription', e.target.value)}
                              placeholder="Enter other description"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button
                    type="button"

                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MedicalHistoryModal;

